import { EventTypes } from '@monorepo/types';
import { trackMixpanel } from '../../mixpanel/mixpanel-client';
import type { BaseEventProperties, EventPropertyMap } from './event-schemas';

/**
 * Type-safe event tracking function with automatic base property injection
 *
 * @example
 * ```typescript
 * // ✅ Correct usage - TypeScript validates properties
 * trackEvent(EventTypes.PAGE_VIEW, {
 *   page_name: HubPageName.VOD,
 *   hub_language: 'en',
 *   url: window.location.href,
 *   screen: 'desktop'
 * });
 *
 * // ❌ Compile error - missing required properties
 * trackEvent(EventTypes.PAGE_VIEW, {
 *   page_name: HubPageName.VOD
 * });
 *
 * // ❌ Compile error - wrong property name
 * trackEvent(EventTypes.PAGE_VIEW, {
 *   wrong_prop: 'test'
 * });
 * ```
 */
export function trackEvent<E extends EventTypes>(
  event: E,
  properties: EventPropertyMap[E & keyof EventPropertyMap],
): void {
  // Base properties are already injected by trackMixpanel
  // (browser, device) so we just pass through
  trackMixpanel({
    event,
    properties: properties as Record<string, unknown>,
  });
}

/**
 * Type helper to extract required properties for an event
 * Useful for creating wrapper functions or hooks
 *
 * @example
 * ```typescript
 * type PageViewProps = RequiredEventProperties<EventTypes.PAGE_VIEW>;
 * // Resolves to: { page_name: HubPageName; hub_language: string; url: string; screen: 'desktop' | 'mobile' }
 * ```
 */
export type RequiredEventProperties<E extends EventTypes> = EventPropertyMap[E &
  keyof EventPropertyMap];

/**
 * Type helper to get all properties including base properties
 *
 * @example
 * ```typescript
 * type FullPageViewProps = AllEventProperties<EventTypes.PAGE_VIEW>;
 * // Includes browser, device, timestamp + page_name, hub_language, url, screen
 * ```
 */
export type AllEventProperties<E extends EventTypes> = BaseEventProperties &
  EventPropertyMap[E & keyof EventPropertyMap];
